import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, clearSessionToken } from "@/lib/api";
import { LANGUAGES } from "@/i18n";
import { logDebug, logWarn } from "@/lib/logger";

const AppContext = createContext(null);

const SUPPORTED_CODES = ["it", "en", "he", "ru"];

function detectInitialLang() {
  const saved = localStorage.getItem("aaron_lang");
  if (saved && SUPPORTED_CODES.includes(saved)) return saved;
  const candidates = [
    ...(navigator.languages || []),
    navigator.language,
    navigator.userLanguage,
  ].filter(Boolean);
  for (const raw of candidates) {
    const code = String(raw).toLowerCase().split(/[-_]/)[0];
    if (SUPPORTED_CODES.includes(code)) return code;
  }
  return "it";
}

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState(detectInitialLang);

  const dir = useMemo(
    () => LANGUAGES.find((l) => l.code === lang)?.dir || "ltr",
    [lang],
  );

  useEffect(() => {
    document.documentElement.setAttribute("dir", dir);
    document.documentElement.setAttribute("lang", lang);
    localStorage.setItem("aaron_lang", lang);
  }, [dir, lang]);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (error) {
      logDebug("Auth check: not authenticated", error?.response?.status);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Skip check if returning from OAuth — AuthCallback handles the session exchange
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      logWarn("Logout API failed, clearing client state anyway", error?.message);
    }
    clearSessionToken();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, setUser, loading, lang, setLang, dir, logout, refreshUser: checkAuth }),
    [user, loading, lang, dir, logout, checkAuth],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export const useApp = () => useContext(AppContext);
